import React from 'react';
import { connect } from 'react-redux';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import get from 'lodash/get';
import defaultPlayerConfig, {
  questionBankPreviewConfig,
  annotationModule,
  blueBookPlayerConfig,
} from 'chameleon/packages/blue-book-player/utility/config';
import { getMathematicsSubjectsConfig } from 'chameleon/packages/blue-book-player/utils';
import addLearnosityPerformance from 'chameleon/packages/blue-book-player/utility/addLearnosityPerformance';

/**
 * Choose the right Learnosity Player configuration.
 * It is used for deciding the configuration to use in players only once.
 * */
const learnosityWrapper =
  (WrappedComponent, injectedProps = {}) =>
  (props) => {
    const {
      isPreviewPlayer,
      showCorrectAnswers,
      noScroll = false,
      removeAnnotations = false,
      isBlueBook = false,
    } = injectedProps;
    const {
      isStudent,
      hasTeacherAnnotation,
      isQBCreateTab,
      isSingleQuestionPreview,
      params,
      disableLazyLoadFF,
      deferRenderFF,
    } = props;
    const componentProps = omit(props, ['hasTeacherAnnotation']);

    let learnosityConfig;
    if (isBlueBook) {
      learnosityConfig = blueBookPlayerConfig;
    } else if (isQBCreateTab && isSingleQuestionPreview) {
      learnosityConfig = addLearnosityPerformance(questionBankPreviewConfig);
    } else {
      learnosityConfig = addLearnosityPerformance(defaultPlayerConfig);
    }

    const isTeacherWithAnnotation = !isStudent && hasTeacherAnnotation;
    if (
      !isPreviewPlayer &&
      (isTeacherWithAnnotation || isStudent) &&
      !removeAnnotations
    ) {
      learnosityConfig = {
        ...learnosityConfig,
        ...annotationModule,
      };
    }

    const qInitOptions = get(
      learnosityConfig,
      'questions_api_init_options',
      {},
    );

    if (showCorrectAnswers) {
      qInitOptions.showCorrectAnswers = true;
    }

    learnosityConfig.questions_api_init_options = {
      ...qInitOptions,
    };

    if (noScroll) {
      learnosityConfig.configuration.focus_on_player = false;
    }

    if (disableLazyLoadFF) {
      learnosityConfig.configuration.lazyload = false;
    }

    learnosityConfig.questions_api_init_options.render_optimization.defer_render =
      deferRenderFF;

    const mathematicsConfig = getMathematicsSubjectsConfig(
      Number.parseInt(params.subject, 10),
    );
    if (mathematicsConfig) {
      learnosityConfig = merge(learnosityConfig, mathematicsConfig);
    }

    return (
      <WrappedComponent
        {...componentProps}
        learnosityConfig={learnosityConfig}
      />
    );
  };

const mapStateToProps = (state) => ({
  isStudent: state.userData.isStudent,
  hasTeacherAnnotation: state.featureFlag['FY-9219-teacher-annotation'],
  disableLazyLoadFF: state.featureFlag.disable_lazy_load,
  deferRenderFF: state.featureFlag.student_player_defer_render,
});

/**
 * withLearnosityConfig - receives injectedProps and wraps a component.
 * It's role is to set the desired Learnosity configuration to all components using Learnosity Player
 * */
export default (injectedProps) => (WrappedComponent) =>
  connect(mapStateToProps)(learnosityWrapper(WrappedComponent, injectedProps));
